import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  IconUser,
  IconUserEdit,
  IconMail,
  IconDeviceMobile,
  IconMapPin,
  IconApps,
  IconNotes,
  IconLink,
  IconFileInvoice,
  IconTicket,
  IconMessage,
  IconPhone,
  IconRefresh,
  IconReceipt,
  IconPlus,
  IconListCheck,
  IconCurrencyDollar,
  IconPrinter,
  IconFileText,
  IconChevronDown,
  IconPhoneCall,
  IconPhoneOff,
  IconPhoneX,
  IconUsers,
  IconStethoscope,
  IconKey,
  IconArrowUp,
  IconArrowDown,
  IconExternalLink,
} from '@tabler/icons-react';
import type { CallDirection, CallKind } from '@techgitter/contracts';
import { resolveAvatarColor, type AvatarColour } from '../../styles/avatarPalette';
import { ActionPicker, type ActionPickerOption } from '../dropdown';
import { CallDirectionIcon } from '../calls/CallDirectionIcon';
import './WorkItemPanel.css';

/** "Log call" action options (Type A — ActionPicker). */
const LOG_CALL_OPTIONS: ActionPickerOption[] = [
  { value: 'log_call', label: 'Log call', Icon: IconPhone },
  { value: 'left_vm', label: 'Left VM for return call', Icon: IconPhoneCall },
  { value: 'no_vm', label: 'Could not leave a VM', Icon: IconPhoneOff },
  { value: 'not_in_service', label: "Customer's phone not in service", Icon: IconPhoneX },
];

/** Comment type options (Type A — ActionPicker). */
const COMMENT_TYPE_OPTIONS: ActionPickerOption[] = [
  { value: 'comment', label: 'Comment', Icon: IconMessage },
  { value: 'standup', label: 'Standup', Icon: IconUsers },
  { value: 'diagnostics', label: 'Diagnostics', Icon: IconStethoscope },
];

/**
 * The five things a technician prints from a work item.
 *
 * A fixed list, deliberately: the Print section is a row of buttons in the same
 * places every time, so an operator reaches for one without reading. An action
 * that can't run for this item is disabled with the reason on hover rather than
 * removed — a button set that reshuffles per ticket is worse than a greyed one
 * that says why.
 *
 * Only the job label is offered twice. The same label goes upstairs to the
 * bench or downstairs to the counter depending on who is about to pick the
 * machine up, and that is the operator's call; everything else has exactly one
 * lane that can render it, resolved from the document by sm4rt-api.
 */
export type PrintAction =
  | 'job-label-upstairs'
  | 'job-label-downstairs'
  | 'customer-label'
  | 'invoice'
  | 'ticket-sheet';

interface PrintActionDef {
  action: PrintAction;
  label: string;
  /** Accessible name + hover text. The two job-label buttons share a visible
   *  label and are told apart by the arrow glyph, so this is what actually
   *  names them — see the nav note in CLAUDE.md on load-bearing titles. */
  title: string;
  Icon: React.ComponentType<{ size?: number; stroke?: number }>;
}

const PRINT_ACTIONS: PrintActionDef[] = [
  {
    action: 'job-label-upstairs',
    label: 'Job label',
    title: 'Print the job label upstairs (workshop)',
    Icon: IconArrowUp,
  },
  {
    action: 'job-label-downstairs',
    label: 'Job label',
    title: 'Print the job label downstairs (counter)',
    Icon: IconArrowDown,
  },
  {
    action: 'customer-label',
    label: 'Customer label',
    title: 'Print the customer label downstairs (counter)',
    Icon: IconUser,
  },
  {
    action: 'invoice',
    label: 'Invoice',
    title: 'Print the A4 tax invoice downstairs',
    Icon: IconFileInvoice,
  },
  {
    action: 'ticket-sheet',
    label: 'Ticket sheet',
    title: 'Print the A4 job ticket sheet downstairs',
    Icon: IconFileText,
  },
];

/** Comment types a user can leave on a job (call is logged separately). */
export type CommentLogType = 'comment' | 'standup' | 'diagnostics';
/** Everything the log input can submit — comment types plus a call. */
export type LogSubmitType = 'call' | CommentLogType;

export interface LogEntry {
  id: string;
  /** 2-char author initials shown in the avatar, e.g. "ST". */
  authorInitials: string;
  /** Stable author id — seeds the avatar colour fallback. */
  authorId: string;
  /** Resolved avatar colour for the author (locked palette). When omitted, a
   *  deterministic colour is derived from `authorId`. */
  authorColor?: AvatarColour;
  source: 'sm4rt' | 'repairshopr';
  /** Display label for the entry type, e.g. "Comment", "Call log". */
  typeLabel: string;
  formattedTime: string;
  text: string;
  /** Set for a 3CX call record: shows its direction and links to the record. */
  call?: {
    /** In-app path to the call, `/call/:id`. Opens in a new tab. */
    href: string;
    direction: CallDirection | null;
    callKind: CallKind | null;
    /** The text says what kind of call it was because there is no summary. */
    noSummary?: boolean;
  };
}

/** A quote linked to this job, shaped for display in the Quotes section. */
export interface QuotePanelItem {
  id: string | number;
  number: string | number;
  name: string;
  /** The price as shown, e.g. "$329.09 ex / $362.00 inc" (`formatQuoteServiceTotal`). */
  totalLabel: string;
  /** Human label for the quote category, e.g. "Repair" / "New Laptop". */
  typeLabel: string;
  status: string;
  /** Drives the status chip colour (mapped from the quote's status). */
  statusTone: 'default' | 'info' | 'success' | 'error' | 'warning' | 'primary';
}

export interface WorkItemPanelProps {
  /* Customer */
  customerName: string;
  /**
   * In-app link to this customer's SM4RT record (`/customer/:id`). Omitted when
   * the work item carries no customer id, in which case the name renders as
   * plain text rather than a link that would land on "Customer not found".
   */
  customerHref?: string;
  customerEmail: string | null;
  customerMobile: string | null;
  customerAddress: string | null;
  customerServices: string[];
  /** Show the admin-only "add service" + next to the Services row. */
  canAddService?: boolean;
  /** Called when the Services + is clicked (opens the add-service modal). */
  onAddService?: () => void;
  /**
   * Reassign this work item's customer. When set, a small edit action appears
   * in the Customer card header. Only wired for job/ticket work items (RS → DV
   * customer change); omitted for appointments/check-ins.
   */
  onChangeCustomer?: () => void;
  customerNotes: string | null;

  /* RepairShopr link URLs */
  repairshoprCustomerUrl: string;
  repairshoprInvoicesUrl: string;
  repairshoprTicketsUrl: string;
  /**
   * Show the "RepairShop Links" section (header + links). Hidden for
   * appointments that have no linked RepairShopr customer, where the links
   * point nowhere useful. Defaults to true.
   */
  showShortcuts?: boolean;

  /* Print */
  /**
   * Run one of the five print actions. When omitted the Print section is
   * hidden entirely (nothing about this item is printable).
   */
  onPrint?: (action: PrintAction) => void;
  /**
   * Why an action can't run, keyed by action. A listed action renders disabled
   * with the reason as its hover text; an unlisted one is live. The reason is
   * shown rather than the button removed — see `PRINT_ACTIONS`.
   */
  printUnavailable?: Partial<Record<PrintAction, string>>;
  /** The action currently in flight; its button shows a pending label. */
  printingAction?: PrintAction | null;

  /** Device section (make/serial) rendered at the top of the job-log card —
   *  set for jobs converted from a check-in. */
  deviceSection?: React.ReactNode;

  /**
   * Open the secure credential-request dialog. Only set for job work items
   * with a RepairShopr ticket id — a credential request binds to a ticket, and
   * the ticket-close purge is what eventually collects the vault item. When
   * omitted the button is hidden.
   */
  onOpenCredentials?: () => void;
  /**
   * Button copy: "Create credential request" until a live request exists on
   * the ticket, "View credential request" after.
   */
  credentialsLabel?: string;

  /* Log */
  logEntries: LogEntry[];
  onLogSubmit: (text: string, type: LogSubmitType) => Promise<void>;
  /** Re-fetch the job log. */
  onRefresh?: () => void;
  /** Disables Save while a submit is in flight. */
  isSubmitting?: boolean;

  /* Sections (V1 — collapsed teasers; only counts/empty state used) */
  quotes?: QuotePanelItem[];
  tasks?: { id: string | number }[];
  lineItems?: { id: string | number }[];
  /** Renders the expandable line-items editor below the Line items header. */
  renderLineItems?: () => React.ReactNode;
  /** Called when the Line items + is clicked (adds a new line item). */
  onAddLineItem?: () => void;
  /** Called when the Line items Sync icon is clicked (re-syncs from RepairShopr). */
  onSyncLineItems?: () => void;
  /** Show the Sync icon in the Line items header (only when there's a linked ticket). */
  canSyncLineItems?: boolean;
  /** Compact summary shown in the Line items header, e.g. "3 items · $180.00". */
  lineItemsSummary?: string;
  /** Renders the task-list picker + checklist below the Task list header. */
  renderTaskList?: () => React.ReactNode;
  /** Called when the Task list + is clicked (opens the task-list picker). */
  onAddTaskList?: () => void;
  /** Compact summary shown in the Task list header, e.g. "2 of 5" / "No task lists". */
  taskListSummary?: string;
  /** Called when the Quotes + is clicked (creates a new prefilled quote). */
  onCreateQuote?: () => void;
  /** Disables the Quotes + while a quote is being created. */
  isCreatingQuote?: boolean;
  /** Navigate to a quote's detail page when its row is clicked. */
  onOpenQuote?: (quoteNumber: string | number) => void;
  /** Show a "Loading…" hint in the Quotes summary while quotes are fetched. */
  quotesLoading?: boolean;
  /** Bumped by the parent to focus the log input and preselect the
   *  Diagnostics comment type (e.g. after declining the no-diagnostics
   *  quote prompt). */
  focusDiagnosticsSignal?: number;
}

/**
 * Expanded work-item detail panel (V1). Two columns: customer info + RepairShopr
 * shortcuts on the left, job log (read + input) and collapsed section teasers on
 * the right. Purely presentational — data and the log-submit handler are props.
 */
export const WorkItemPanel: React.FC<WorkItemPanelProps> = ({
  customerName,
  customerHref,
  customerEmail,
  customerMobile,
  customerAddress,
  customerServices,
  canAddService,
  onAddService,
  onChangeCustomer,
  customerNotes,
  repairshoprCustomerUrl,
  repairshoprInvoicesUrl,
  repairshoprTicketsUrl,
  showShortcuts = true,
  onPrint,
  printUnavailable,
  printingAction,
  deviceSection,
  onOpenCredentials,
  credentialsLabel,
  logEntries,
  onLogSubmit,
  onRefresh,
  isSubmitting,
  quotes = [],
  renderLineItems,
  onAddLineItem,
  onSyncLineItems,
  canSyncLineItems,
  lineItemsSummary,
  renderTaskList,
  onAddTaskList,
  taskListSummary,
  onCreateQuote,
  isCreatingQuote,
  onOpenQuote,
  quotesLoading,
  focusDiagnosticsSignal,
}) => {
  const [logText, setLogText] = useState('');
  // The comment type chosen in the dropdown; calls are logged via the preset picker.
  const [commentType, setCommentType] = useState<CommentLogType>('comment');
  const [lineItemsExpanded, setLineItemsExpanded] = useState(false);
  const [taskListExpanded, setTaskListExpanded] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // When the parent bumps the signal (e.g. the tech declined the
  // no-diagnostics quote prompt), switch the comment type to Diagnostics and
  // focus the input so they can post one straight away.
  const lastFocusDiagnosticsSignal = useRef(focusDiagnosticsSignal);
  useEffect(() => {
    if (
      focusDiagnosticsSignal === undefined ||
      focusDiagnosticsSignal === lastFocusDiagnosticsSignal.current
    ) {
      return;
    }
    lastFocusDiagnosticsSignal.current = focusDiagnosticsSignal;
    setCommentType('diagnostics');
    textareaRef.current?.focus();
  }, [focusDiagnosticsSignal]);

  // "Log call" custom dropdown. `lastCallType` drives the active highlight only
  // (default `log_call`); picking a preset submits a `call` entry immediately.
  const [logCallOpen, setLogCallOpen] = useState(false);
  const [lastCallType, setLastCallType] = useState('log_call');
  const logCallTriggerRef = useRef<HTMLButtonElement>(null);

  // Comment-type custom dropdown. The selected `commentType` is what Save submits.
  const [commentTypeOpen, setCommentTypeOpen] = useState(false);
  const commentTypeTriggerRef = useRef<HTMLButtonElement>(null);

  const handleTypeChange = (type: CommentLogType) => {
    setCommentType(type);
    textareaRef.current?.focus();
  };

  const handleSave = async () => {
    const text = logText.trim();
    if (!text) return;
    await onLogSubmit(text, commentType);
    setLogText('');
  };

  const handleLogCallSelect = (value: string) => {
    setLastCallType(value);
    // `log_call` is the header/no-op; the others log a `call` entry immediately.
    if (value === 'log_call') return;
    const option = LOG_CALL_OPTIONS.find((o) => o.value === value);
    if (option) void onLogSubmit(option.label, 'call');
  };

  return (
    <div className="work-item-expanded__body">
      {/* ── Card A — customer info ── */}
      <div className="work-item-expanded__customer-card">
        <div className="panel-section-header">
          <IconUser className="panel-section-header__icon" size={18} stroke={1.5} />
          <span className="panel-section-header__title">Customer</span>
          {onChangeCustomer && (
            <IconUserEdit
              size={16}
              stroke={1.5}
              className="panel-section-header__action"
              role="button"
              aria-label="Change customer"
              title="Change customer"
              onClick={onChangeCustomer}
            />
          )}
        </div>

        <div className="panel-info-row">
          <span className="panel-info-row__label"><IconUser size={16} stroke={1.5} /> Name</span>
          {customerHref ? (
            <Link className="panel-info-row__value panel-info-row__value--link" to={customerHref} title={customerName}>
              {customerName}
            </Link>
          ) : (
            <span className="panel-info-row__value" title={customerName}>{customerName}</span>
          )}
        </div>

        <div className="panel-info-row">
          <span className="panel-info-row__label"><IconMail size={16} stroke={1.5} /> Email</span>
          {customerEmail ? (
            <a className="panel-info-row__value panel-info-row__value--link" href={`mailto:${customerEmail}`} title={customerEmail}>
              {customerEmail}
            </a>
          ) : (
            <span className="panel-info-row__value panel-info-row__value--empty">—</span>
          )}
        </div>

        <div className="panel-info-row">
          <span className="panel-info-row__label"><IconDeviceMobile size={16} stroke={1.5} /> Mobile</span>
          {customerMobile ? (
            <a className="panel-info-row__value panel-info-row__value--link" href={`tel:${customerMobile}`} title={customerMobile}>
              {customerMobile}
            </a>
          ) : (
            <span className="panel-info-row__value panel-info-row__value--empty">—</span>
          )}
        </div>

        <div className="panel-info-row">
          <span className="panel-info-row__label"><IconMapPin size={16} stroke={1.5} /> Address</span>
          {customerAddress ? (
            <span className="panel-info-row__value" title={customerAddress}>{customerAddress}</span>
          ) : (
            <span className="panel-info-row__value panel-info-row__value--empty">—</span>
          )}
        </div>

        <div className="panel-info-row">
          <span className="panel-info-row__label"><IconApps size={16} stroke={1.5} /> Services</span>
          <span className="panel-info-row__value">
            {customerServices.length > 0 ? (
              customerServices.map((s) => (
                <span key={s} className="panel-service-chip">{s}</span>
              ))
            ) : (
              <span className="panel-info-row__value--empty">—</span>
            )}
          </span>
          {canAddService && (
            <IconPlus
              size={16}
              stroke={1.5}
              className="panel-service-add"
              role="button"
              aria-label="Add service"
              title="Add service"
              onClick={onAddService}
            />
          )}
        </div>

        <div className="panel-info-row">
          <span className="panel-info-row__label"><IconNotes size={16} stroke={1.5} /> Notes</span>
          {customerNotes ? (
            <span className="panel-info-row__value" title={customerNotes}>{customerNotes}</span>
          ) : (
            <span className="panel-info-row__value panel-info-row__value--empty">No notes</span>
          )}
        </div>

        {showShortcuts && (
          <>
            <div className="panel-section-header" style={{ marginTop: 4 }}>
              <IconLink className="panel-section-header__icon" size={18} stroke={1.5} />
              <span className="panel-section-header__title">RepairShop Links</span>
            </div>

            <div className="panel-shortcuts">
              <a className="panel-shortcut" href={repairshoprCustomerUrl} target="_blank" rel="noopener noreferrer">
                <IconUser size={18} stroke={1.5} />
                <span>Customer</span>
              </a>
              <a className="panel-shortcut" href={repairshoprInvoicesUrl} target="_blank" rel="noopener noreferrer">
                <IconFileInvoice size={18} stroke={1.5} />
                <span>Invoices</span>
              </a>
              <a className="panel-shortcut" href={repairshoprTicketsUrl} target="_blank" rel="noopener noreferrer">
                <IconTicket size={18} stroke={1.5} />
                <span>Tickets</span>
              </a>
            </div>
          </>
        )}

        {onPrint && (
          <>
            <div className="panel-section-header" style={{ marginTop: 4 }}>
              <IconPrinter className="panel-section-header__icon" size={18} stroke={1.5} />
              <span className="panel-section-header__title">Print</span>
            </div>

            <div className="panel-shortcuts">
              {PRINT_ACTIONS.map(({ action, label, title, Icon }) => {
                const reason = printUnavailable?.[action];
                const pending = printingAction === action;
                return (
                  <button
                    key={action}
                    type="button"
                    className="panel-shortcut"
                    onClick={() => onPrint(action)}
                    disabled={Boolean(reason) || printingAction != null}
                    title={reason ?? title}
                    aria-label={title}
                  >
                    <Icon size={18} stroke={1.5} />
                    <span>{pending ? 'Printing…' : label}</span>
                  </button>
                );
              })}
            </div>
          </>
        )}
      </div>

      {/* ── Card B — job log + sections ── */}
      <div className="work-item-expanded__log-card">
        {deviceSection}
        <div className="panel-section-header">
          <IconMessage className="panel-section-header__icon" size={18} stroke={1.5} />
          <span className="panel-section-header__title">
            Job log · {logEntries.length} {logEntries.length === 1 ? 'entry' : 'entries'}
          </span>
        </div>

        <div className="panel-log-entries">
          {logEntries.map((entry) => {
            const colour =
              entry.authorColor ?? resolveAvatarColor({ seed: entry.authorId });
            return (
            <div className="log-entry" key={entry.id}>
              <div
                className="log-entry__avatar"
                style={{ background: colour.bg, color: colour.fg }}
              >
                {entry.authorInitials}
              </div>
              <div className="log-entry__body">
                <div className="log-entry__meta">
                  {entry.call && <CallDirectionIcon call={entry.call} size={14} />}
                  <span className="log-entry__type-chip">{entry.typeLabel}</span>
                  {entry.source === 'repairshopr' && (
                    <span className="log-entry__rs-tag" title="Synced from RepairShopr">RS</span>
                  )}
                  <span className="log-entry__time">{entry.formattedTime}</span>
                  {entry.call && (
                    <a
                      className="log-entry__call-link"
                      href={entry.call.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label="Open call record"
                      title="Open call record"
                    >
                      <IconExternalLink size={14} stroke={1.5} />
                    </a>
                  )}
                </div>
                <div
                  className={
                    entry.call?.noSummary
                      ? 'log-entry__text log-entry__text--muted'
                      : 'log-entry__text'
                  }
                >
                  {entry.text}
                </div>
              </div>
            </div>
            );
          })}
          {logEntries.length === 0 && <p className="panel-log-empty">No log entries yet.</p>}
        </div>

        <div className="panel-log-input">
          <textarea
            ref={textareaRef}
            className="panel-log-textarea"
            placeholder="What's next for this job?"
            value={logText}
            onChange={(e) => setLogText(e.target.value)}
          />
          <div className="panel-log-actions">
            {onOpenCredentials && (
              <button
                type="button"
                className="panel-log-trigger"
                onClick={onOpenCredentials}
                title={credentialsLabel}
              >
                <IconKey size={14} stroke={1.5} />
                <span className="panel-log-trigger__label">{credentialsLabel}</span>
              </button>
            )}
            <button
              type="button"
              ref={logCallTriggerRef}
              className="panel-log-trigger"
              onClick={() => setLogCallOpen((v) => !v)}
            >
              <IconPhone size={14} stroke={1.5} />
              <span className="panel-log-trigger__label">Log call</span>
              <IconChevronDown size={12} stroke={1.5} className="panel-log-trigger__chevron" />
            </button>
            {logCallOpen && (
              <ActionPicker
                options={LOG_CALL_OPTIONS}
                currentValue={lastCallType}
                onSelect={handleLogCallSelect}
                onClose={() => setLogCallOpen(false)}
                anchorRef={logCallTriggerRef}
                preferAbove
              />
            )}
            {(() => {
              const selected = COMMENT_TYPE_OPTIONS.find((o) => o.value === commentType);
              const SelectedIcon = selected?.Icon ?? IconMessage;
              return (
                <button
                  type="button"
                  ref={commentTypeTriggerRef}
                  className="panel-log-trigger"
                  onClick={() => setCommentTypeOpen((v) => !v)}
                >
                  <SelectedIcon size={14} stroke={1.5} />
                  <span className="panel-log-trigger__label">{selected?.label ?? 'Comment'}</span>
                  <IconChevronDown size={12} stroke={1.5} className="panel-log-trigger__chevron" />
                </button>
              );
            })()}
            {commentTypeOpen && (
              <ActionPicker
                options={COMMENT_TYPE_OPTIONS}
                currentValue={commentType}
                onSelect={(value) => handleTypeChange(value as CommentLogType)}
                onClose={() => setCommentTypeOpen(false)}
                anchorRef={commentTypeTriggerRef}
                preferAbove
              />
            )}
            <button
              type="button"
              className="panel-log-btn panel-log-btn--icon"
              onClick={onRefresh}
              title="Refresh log"
            >
              <IconRefresh size={14} stroke={1.5} />
            </button>
            <button
              type="button"
              className="panel-log-btn panel-log-btn--save"
              disabled={!logText.trim() || isSubmitting}
              onClick={handleSave}
            >
              Save
            </button>
          </div>
        </div>

        <div className="panel-collapsed-section">
          <span className="panel-collapsed-section__title">
            <IconReceipt size={14} stroke={1.5} /> Quotes
            <span className="panel-collapsed-section__summary">
              · {quotes.length > 0
                ? `${quotes.length} quote${quotes.length === 1 ? '' : 's'}`
                : quotesLoading
                ? 'Loading…'
                : 'No quotes'}
            </span>
          </span>
          <IconPlus
            size={16}
            stroke={1.5}
            className="panel-collapsed-section__add"
            role="button"
            aria-label="New quote"
            onClick={() => {
              if (!isCreatingQuote) onCreateQuote?.();
            }}
          />
        </div>
        {quotes.length > 0 && (
          <div className="panel-quotes">
            {quotes.map((quote) => (
              <div
                key={quote.id}
                className="panel-quote-row"
                role="button"
                tabIndex={0}
                onClick={() => onOpenQuote?.(quote.number)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    onOpenQuote?.(quote.number);
                  }
                }}
              >
                <div className="panel-quote-row__main">
                  <span className="panel-quote-row__number">Quote #{quote.number}</span>
                  <span className="panel-quote-row__name">{quote.name}</span>
                </div>
                <div className="panel-quote-row__meta">
                  <span className="panel-quote-row__total">{quote.totalLabel}</span>
                  <span className="panel-quote-chip">{quote.typeLabel}</span>
                  <span className={`panel-quote-chip panel-quote-chip--${quote.statusTone}`}>
                    {quote.status}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}

        <div
          className="panel-collapsed-section"
          onClick={() => setTaskListExpanded((e) => !e)}
        >
          <span className="panel-collapsed-section__title">
            <IconListCheck size={14} stroke={1.5} /> Task list
            {taskListSummary && (
              <span className="panel-collapsed-section__summary">· {taskListSummary}</span>
            )}
          </span>
          <span className="panel-collapsed-section__actions">
            <IconPlus
              size={16}
              stroke={1.5}
              className="panel-collapsed-section__add"
              role="button"
              aria-label="Add task list"
              title="Add task list"
              onClick={(e) => {
                e.stopPropagation();
                setTaskListExpanded(true);
                onAddTaskList?.();
              }}
            />
          </span>
        </div>
        {/* Mounted even while collapsed (display toggle) so the header summary
            stays live and the + acts on the mounted picker. */}
        <div style={{ display: taskListExpanded ? undefined : 'none' }}>
          {renderTaskList?.()}
        </div>

        <div
          className="panel-collapsed-section"
          onClick={() => setLineItemsExpanded((e) => !e)}
        >
          <span className="panel-collapsed-section__title">
            <IconCurrencyDollar size={14} stroke={1.5} /> Line items
            {lineItemsSummary && (
              <span className="panel-collapsed-section__summary">· {lineItemsSummary}</span>
            )}
          </span>
          {/* Sync + add icons stop propagation so only clicks elsewhere on the
              row toggle the editor open/closed. */}
          <span className="panel-collapsed-section__actions">
            {canSyncLineItems && (
              <IconRefresh
                size={16}
                stroke={1.5}
                className="panel-collapsed-section__add"
                role="button"
                aria-label="Sync line items"
                title="Sync"
                onClick={(e) => {
                  e.stopPropagation();
                  onSyncLineItems?.();
                }}
              />
            )}
            <IconPlus
              size={16}
              stroke={1.5}
              className="panel-collapsed-section__add"
              role="button"
              aria-label="Add line item"
              title="Add line item"
              onClick={(e) => {
                e.stopPropagation();
                setLineItemsExpanded(true);
                onAddLineItem?.();
              }}
            />
          </span>
        </div>
        {/* Mounted even while collapsed (display toggle) so the header's +/Sync
            act on the live editor and its summary stays current. */}
        <div style={{ display: lineItemsExpanded ? undefined : 'none' }}>
          {renderLineItems?.()}
        </div>
      </div>
    </div>
  );
};
